/**
 * The apartment edit page used to have ten tabs; they are now five groups of the same sections
 * (Issue #137, Variant 4 of #132). The sections themselves — and what each one saves — did not change.
 *
 * <p>Two vocabularies live side by side on purpose:
 * <ul>
 *   <li>{@link SectionKey}: the old tab keys. Still what `?tab=` links elsewhere in the app use
 *       (`?tab=calendar`, `?tab=citytax`), so they must keep resolving forever.</li>
 *   <li>{@link GroupKey}: what a tab button shows and what the URL now gets written with.</li>
 * </ul>
 *
 * <p>`booking` (the direct-booking web) is neither: it is still private, belongs to no group, is not
 * searchable and has no button. It stays reachable by URL only, exactly as before — see
 * {@link BOOKING_VIEW}.
 */

export type SectionKey =
  | 'general' | 'contact' | 'calendar' | 'guide'
  | 'payments' | 'citytax' | 'deposit' | 'upsell'
  | 'ubyport' | 'export';

/** Every key `?tab=` has ever accepted. */
export type TabKey = SectionKey | 'booking';

export type GroupKey = 'basic' | 'reservations' | 'arrival' | 'money' | 'authorities';

/** The special "no group selected, only the direct-booking block" state. */
export const BOOKING_VIEW = 'booking' as const;
export type ViewKey = GroupKey | typeof BOOKING_VIEW;

export const GROUP_ORDER: GroupKey[] = ['basic', 'reservations', 'arrival', 'money', 'authorities'];

/** Sections of each group, in the order they are shown. */
export const GROUP_SECTIONS: Record<GroupKey, SectionKey[]> = {
  basic: ['general', 'contact'],
  reservations: ['calendar'],
  arrival: ['guide'],
  money: ['payments', 'citytax', 'deposit', 'upsell'],
  authorities: ['ubyport', 'export'],
};

export const SECTION_GROUP: Record<SectionKey, GroupKey> = {
  general: 'basic',
  contact: 'basic',
  calendar: 'reservations',
  guide: 'arrival',
  payments: 'money',
  citytax: 'money',
  deposit: 'money',
  upsell: 'money',
  ubyport: 'authorities',
  export: 'authorities',
};

export type IconName = 'home' | 'calendar' | 'key' | 'wallet' | 'landmark';

export const GROUP_ICON: Record<GroupKey, IconName> = {
  basic: 'home',
  reservations: 'calendar',
  arrival: 'key',
  money: 'wallet',
  authorities: 'landmark',
};

export interface NavTarget {
  group: ViewKey;
  /** Set when the param named an old section: the page scrolls to it after switching group. */
  section?: SectionKey;
}

const SECTION_KEYS = Object.keys(SECTION_GROUP) as SectionKey[];

/**
 * Turns a `?tab=` value into where the page should be. Accepts the new group keys and every old tab
 * key; anything else (missing, misspelt, from a future version) lands on the first group.
 */
export function resolveTabParam(param: string | null | undefined): NavTarget {
  if (param === BOOKING_VIEW) return { group: BOOKING_VIEW };
  if (param && (GROUP_ORDER as string[]).includes(param)) return { group: param as GroupKey };
  if (param && (SECTION_KEYS as string[]).includes(param)) {
    const section = param as SectionKey;
    return { group: SECTION_GROUP[section], section };
  }
  return { group: 'basic' };
}

/** Same as above, but the Doručenky download anchor (`#dorucenka-download`) wins over `?tab=`. */
export function resolveLocation(param: string | null | undefined, hash: string | null | undefined): NavTarget & { anchorId?: string } {
  if (hash === '#dorucenka-download') return { group: 'authorities', section: 'ubyport', anchorId: 'dorucenka-download' };
  return resolveTabParam(param);
}

/** Groups whose sections hold a field saved through the shared "Uložit" button. */
export const GROUPS_WITH_SAVE_BAR: GroupKey[] = ['basic', 'arrival', 'money', 'authorities'];

/** Old tab labels carry a leading emoji ("💳 Platby"); a section heading wants the words only. */
export function stripLeadingSymbols(label: string): string {
  return label.replace(/^[^\p{L}\p{N}]+/u, '').replace(/[:：]\s*$/, '').trim();
}

/**
 * Where to scroll after landing on a group, or `null` when landing on the group is enough.
 * The first section of a group sits at its top already, so `?tab=general` or `?tab=ubyport` do not
 * scroll; `?tab=citytax`, `?tab=contact` and the Doručenky anchor do.
 */
export function scrollRequestFor(
  nav: NavTarget & { anchorId?: string },
): { group: GroupKey; section: SectionKey; targetId?: string } | null {
  if (nav.group === BOOKING_VIEW || !nav.section) return null;
  if (nav.anchorId) return { group: nav.group, section: nav.section, targetId: nav.anchorId };
  if (GROUP_SECTIONS[nav.group][0] === nav.section) return null;
  return { group: nav.group, section: nav.section };
}
